import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Check, Trash2, Pill, X, FileText, ReceiptText, RefreshCw,
  ShoppingBag, Store, Truck, MapPin, Loader2, CheckCircle2, Clock, XCircle, CalendarClock, BarChart3,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { medicationApi, pharmacyApi } from "@/lib/api";

const TEAL = "#059669";

const FREQS = [
  { value: "once_daily", label: "Once daily" },
  { value: "twice_daily", label: "Twice daily" },
  { value: "thrice_daily", label: "Thrice daily" },
  { value: "four_times", label: "Four times a day" },
];

const ORDER_STATUS = {
  PENDING_PHARMACY: { label: "Pending pharmacy", cls: "bg-amber-100 text-amber-700", icon: Clock },
  ACCEPTED: { label: "Accepted", cls: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  FULFILLED: { label: "Fulfilled", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  REJECTED: { label: "Rejected", cls: "bg-red-100 text-red-700", icon: XCircle },
  CANCELLED: { label: "Cancelled", cls: "bg-slate-100 text-slate-600", icon: XCircle },
};

const TABS = [
  { key: "pills", label: "Pills", icon: Pill },
  { key: "prescriptions", label: "Prescriptions", icon: FileText },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "receipts", label: "Receipts", icon: ReceiptText },
];

function itemsSummary(items) {
  if (!items || items.length === 0) return "Prescription attached";
  return items.map((i) => `${i.name}${i.dosage ? ` ${i.dosage}` : ""}`).join(", ");
}

export default function Medications() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const patientId = params.get("patient") || "";
  const patientName = params.get("patientName") || "";
  const uid = patientId || user?._id;
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "pills";
  const setTab = (k) => setParams((p) => { p.set("tab", k); return p; }, { replace: true });

  const [reorder, setReorder] = useState(null);

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="med-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {(tab === "orders" || tab === "prescriptions") && (
          <Button onClick={() => setReorder({ sourcePrescriptionId: null, items: [{ name: "", dosage: "", quantity: "" }] })}
            data-testid="new-reorder-btn" className="rounded-full gap-1.5" style={{ backgroundColor: TEAL }}>
            <Plus className="h-4 w-4" /> New reorder
          </Button>
        )}
      </div>

      <h1 className="text-2xl font-extrabold flex items-center gap-2" style={{ color: TEAL }}><Pill className="h-6 w-6" /> Medications</h1>

      {patientName && tab === "pills" && (
        <div className="rounded-xl px-4 py-2.5 text-sm font-bold" style={{ backgroundColor: "#FFF7ED", border: "1px solid #FED7AA", color: "#B45309" }} data-testid="med-managing-banner">
          👤 Managing {patientName}&apos;s medications
        </div>
      )}

      {/* Primary tabs */}
      <div className="flex gap-1.5 p-1 rounded-2xl bg-muted">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-semibold transition-colors ${active ? "text-white" : "text-muted-foreground"}`}
              style={active ? { backgroundColor: TEAL } : {}}>
              <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {tab === "pills" && <PillsTab uid={uid} />}
      {tab === "prescriptions" && <PharmacyTab uid={uid} kind="prescriptions" onReorder={setReorder} />}
      {tab === "orders" && <PharmacyTab uid={uid} kind="orders" onReorder={setReorder} />}
      {tab === "receipts" && <PharmacyTab uid={uid} kind="receipts" />}

      {reorder && (
        <ReorderDialog state={reorder} uid={uid} onClose={() => setReorder(null)}
          onDone={() => { setReorder(null); setTab("orders"); }} />
      )}
    </div>
  );
}

/* ============================== PILLS (manager) ============================== */
function PillsTab({ uid }) {
  const [meds, setMeds] = useState([]);
  const [schedule, setSchedule] = useState({ morning: [], afternoon: [], evening: [] });
  const [stats, setStats] = useState({ taken_doses: 0, total_doses: 0 });
  const [adherence7d, setAdherence7d] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", dosage: "", frequency: "once_daily", condition: "", quantity_total: "" });
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState("meds");

  const load = useCallback(async () => {
    if (!uid) { setLoading(false); return; }
    try {
      const [listR, schedR, adhR] = await Promise.allSettled([
        medicationApi.list(uid), medicationApi.schedule(uid), medicationApi.adherence(uid, 7),
      ]);
      if (listR.status === "fulfilled") setMeds(listR.value.data?.medications || []);
      if (schedR.status === "fulfilled") {
        setSchedule(schedR.value.data?.schedule || { morning: [], afternoon: [], evening: [] });
        setStats(schedR.value.data?.stats || {});
      }
      if (adhR.status === "fulfilled") setAdherence7d(adhR.value.data?.adherence?.percentage ?? adhR.value.data?.adherence_pct ?? null);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const markTaken = async (item) => {
    await medicationApi.logDose({ user_id: uid, medication_id: item.medication_id, scheduled_time: item.time, action: "taken" });
    toast.success("Dose logged"); load();
  };
  const addMed = async () => {
    if (!form.name.trim()) { toast.error("Enter a medicine name"); return; }
    setSaving(true);
    try {
      await medicationApi.add(uid, {
        name: form.name.trim(), dosage: form.dosage, frequency: form.frequency,
        condition: form.condition, quantity_total: form.quantity_total ? parseInt(form.quantity_total) : null,
      });
      toast.success(`${form.name} added`); setShowAdd(false);
      setForm({ name: "", dosage: "", frequency: "once_daily", condition: "", quantity_total: "" }); load();
    } catch { toast.error("Could not add medication"); } finally { setSaving(false); }
  };
  const removeMed = async (id) => {
    setMeds((m) => m.filter((x) => x.id !== id));
    try { await medicationApi.remove(id); load(); } catch { load(); }
  };

  const renderSlot = (title, items) => items.length === 0 ? null : (
    <div key={title}>
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">{title}</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <Card key={i} className="flex items-center gap-3 rounded-2xl p-3">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: TEAL + "1A" }}><Pill className="h-4 w-4" style={{ color: TEAL }} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{it.name} {it.dosage && <span className="text-muted-foreground font-normal">· {it.dosage}</span>}</p>
              <p className="text-xs text-muted-foreground">{it.time}{it.condition ? ` · ${it.condition}` : ""}</p>
            </div>
            {it.status === "taken" ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><Check className="h-4 w-4" /> Taken</span>
            ) : (
              <button onClick={() => markTaken(it)} data-testid={`med-take-${it.medication_id}-${it.time}`} className="rounded-xl px-3 py-1.5 text-xs font-bold text-white" style={{ backgroundColor: TEAL }}>Mark taken</button>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  if (loading) return <Skeleton className="h-40 rounded-3xl" />;

  const SUBS = [{ k: "meds", label: "My Meds", icon: Pill }, { k: "schedule", label: "Schedule", icon: CalendarClock }, { k: "adherence", label: "Adherence", icon: BarChart3 }];
  const hasSchedule = (schedule.morning.length + schedule.afternoon.length + schedule.evening.length) > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {SUBS.map((x) => (
            <button key={x.k} onClick={() => setSub(x.k)} data-testid={`pills-sub-${x.k}`}
              className={`text-sm font-semibold px-3 py-1.5 rounded-full ${sub === x.k ? "text-white" : "bg-muted text-muted-foreground"}`}
              style={sub === x.k ? { backgroundColor: TEAL } : {}}>{x.label}</button>
          ))}
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="med-add-btn" size="sm" className="rounded-full gap-1" style={{ backgroundColor: TEAL }}><Plus className="h-4 w-4" /> Add</Button>
      </div>

      {sub === "meds" && (
        meds.length === 0 ? (
          <Card className="rounded-3xl p-8 text-center border-dashed text-muted-foreground"><Pill className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No medications yet. Tap <b>Add</b> to start tracking.</p></Card>
        ) : (
          <div className="space-y-2">
            {meds.map((m) => (
              <Card key={m.id} data-testid={`med-row-${m.id}`} className="flex items-center gap-3 rounded-2xl p-4">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: TEAL + "1A" }}><Pill className="h-5 w-5" style={{ color: TEAL }} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{m.name} {m.dosage && <span className="text-muted-foreground font-normal">· {m.dosage}</span>}</p>
                  <p className="text-xs text-muted-foreground">{(m.time_slots || []).join(", ")}{m.condition ? ` · ${m.condition}` : ""}{m.quantity_remaining != null ? ` · ${m.quantity_remaining} left` : ""}</p>
                </div>
                <button onClick={() => removeMed(m.id)} data-testid={`med-remove-${m.id}`} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </Card>
            ))}
          </div>
        )
      )}

      {sub === "schedule" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl p-4" style={{ backgroundColor: TEAL + "14" }}>
              <p className="text-xs font-bold text-slate-600">Today&apos;s progress</p>
              <p className="text-2xl font-extrabold mt-1" style={{ color: TEAL }}>{stats.taken_doses || 0}/{stats.total_doses || 0}</p>
              <p className="text-xs text-slate-600">doses taken</p>
            </div>
            <div className="rounded-2xl p-4 bg-emerald-50">
              <p className="text-xs font-bold text-slate-600">7-day adherence</p>
              <p className="text-2xl font-extrabold mt-1 text-emerald-700">{adherence7d != null ? `${adherence7d}%` : "—"}</p>
              <p className="text-xs text-slate-600">{adherence7d == null ? "No data yet" : adherence7d >= 80 ? "Good" : "Keep it up"}</p>
            </div>
          </div>
          {hasSchedule ? (<>{renderSlot("Morning", schedule.morning)}{renderSlot("Afternoon", schedule.afternoon)}{renderSlot("Evening", schedule.evening)}</>) : (
            <Card className="rounded-3xl p-8 text-center border-dashed text-muted-foreground"><CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No doses scheduled today.</p></Card>
          )}
        </div>
      )}

      {sub === "adherence" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-4" style={{ backgroundColor: TEAL + "14" }}>
            <p className="text-xs font-bold text-slate-600">7-day adherence</p>
            <p className="text-3xl font-extrabold mt-1" style={{ color: TEAL }}>{adherence7d != null ? `${adherence7d}%` : "—"}</p>
          </div>
          <div className="rounded-2xl p-4 bg-emerald-50">
            <p className="text-xs font-bold text-slate-600">Today</p>
            <p className="text-3xl font-extrabold mt-1 text-emerald-700">{stats.taken_doses || 0}/{stats.total_doses || 0}</p>
            <p className="text-xs text-slate-600">doses taken</p>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-md rounded-3xl bg-card p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="text-lg font-extrabold">Add medication</h3><button onClick={() => setShowAdd(false)}><X className="h-5 w-5 text-muted-foreground" /></button></div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="med-form-name" placeholder="Medicine name *" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-3">
              <Input value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder="Dosage (e.g. 5mg)" className="rounded-xl" />
              <Input value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: e.target.value })} type="number" placeholder="Qty (optional)" className="rounded-xl" />
            </div>
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="med-form-frequency" className="w-full rounded-xl border border-border bg-background px-3 h-11 text-sm">
              {FREQS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} placeholder="Condition (what's it for?)" className="rounded-xl" />
            <Button onClick={addMed} disabled={saving} data-testid="med-form-save" className="w-full rounded-xl" style={{ backgroundColor: TEAL }}>{saving ? "Saving…" : "Add medication"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================== PHARMACY (prescriptions / orders / receipts) ============================== */
function PharmacyTab({ uid, kind, onReorder }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const load = useCallback(async () => {
    if (!uid) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = kind === "prescriptions" ? await pharmacyApi.prescriptions(uid)
        : kind === "orders" ? await pharmacyApi.orders(uid)
        : await pharmacyApi.receipts(uid);
      setData(res.data?.[kind] || res.data?.receipts || []);
    } catch { setData([]); } finally { setLoading(false); }
  }, [uid, kind]);
  useEffect(() => { load(); }, [load]);

  const cancelOrder = async (id) => {
    setData((o) => o.map((x) => (x.id === id ? { ...x, status: "CANCELLED" } : x)));
    try { await pharmacyApi.cancelOrder(id); toast.success("Order cancelled"); load(); } catch { toast.error("Could not cancel"); load(); }
  };
  const openReorder = (rx) => {
    const items = (rx?.items || []).map((i) => ({ name: i.name || i.drug || "", dosage: i.dosage || i.strength || "", quantity: i.quantity || i.qty || "" }));
    onReorder({ sourcePrescriptionId: rx?.id || null, items: items.length ? items : [{ name: "", dosage: "", quantity: "" }] });
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-24 rounded-3xl" /><Skeleton className="h-24 rounded-3xl" /></div>;

  if (kind === "prescriptions") {
    return data.length === 0 ? <Empty icon={FileText} title="No prescriptions yet" text="Prescriptions from your doctor or hospital appear here. You can still start a reorder manually." />
      : <div className="space-y-3">{data.map((rx) => (
        <Card key={rx.id} data-testid={`rx-${rx.id}`} className="rounded-3xl p-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL + "1A" }}><FileText className="h-5 w-5" style={{ color: TEAL }} /></div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{rx.doctorName || "Prescription"}</p>
              <p className="text-sm text-muted-foreground line-clamp-2">{itemsSummary(rx.items)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{rx.createdAt ? new Date(rx.createdAt).toLocaleDateString() : ""}{rx.status ? ` • ${rx.status}` : ""}</p>
            </div>
          </div>
          <Button onClick={() => openReorder(rx)} data-testid={`reorder-${rx.id}`} variant="outline" className="mt-3 w-full rounded-2xl gap-1.5" style={{ color: TEAL, borderColor: TEAL + "55" }}><RefreshCw className="h-4 w-4" /> Reorder from a pharmacy</Button>
        </Card>
      ))}</div>;
  }

  if (kind === "orders") {
    return data.length === 0 ? <Empty icon={ShoppingBag} title="No reorders yet" text="Tap “New reorder” to send a medicine request to a nearby partner pharmacy." />
      : <div className="space-y-3">{data.map((o) => {
        const s = ORDER_STATUS[o.status] || { label: o.status, cls: "bg-slate-100 text-slate-600", icon: Clock };
        const Icon = s.icon;
        return (
          <Card key={o.id} data-testid={`order-${o.id}`} className="rounded-3xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL + "1A" }}><Store className="h-5 w-5" style={{ color: TEAL }} /></div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{o.pharmacyName || "Pharmacy"}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{itemsSummary(o.items)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{o.orderNumber} • {o.deliveryMode === "delivery" ? "Home delivery" : "Pickup"}{o.totalAmount ? ` • ₹${o.totalAmount}` : ""}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${s.cls}`}><Icon className="h-3 w-3" /> {s.label}</span>
            </div>
            {o.rejectionReason && <p className="text-xs text-red-600 mt-2">{o.rejectionReason}</p>}
            {o.pharmacyNotes && <p className="text-xs text-muted-foreground mt-2">Pharmacy: {o.pharmacyNotes}</p>}
            {["PENDING_PHARMACY", "ACCEPTED"].includes(o.status) && (
              <Button onClick={() => cancelOrder(o.id)} data-testid={`cancel-${o.id}`} variant="ghost" className="mt-2 text-red-600 h-8 px-2 text-sm">Cancel order</Button>
            )}
          </Card>
        );
      })}</div>;
  }

  // receipts
  return data.length === 0 ? <Empty icon={ReceiptText} title="No receipts yet" text="Paid consultations and fulfilled medicine orders will show here with their amounts." />
    : <div className="space-y-3">{data.map((r) => (
      <Card key={r.id} data-testid={`receipt-${r.id}`} className="rounded-3xl p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL + "1A" }}><ReceiptText className="h-5 w-5" style={{ color: TEAL }} /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{r.title}</p>
          <p className="text-xs text-muted-foreground truncate">{r.subtitle || r.type}{r.date ? ` • ${new Date(r.date).toLocaleDateString()}` : ""}{r.method ? ` • ${String(r.method).toUpperCase()}` : ""}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold" style={{ color: TEAL }}>{r.amount != null ? `₹${r.amount}` : "—"}</p>
          <p className="text-[10px] font-semibold text-emerald-600">{r.isPaid ? "PAID" : "DUE"}</p>
        </div>
      </Card>
    ))}</div>;
}

function Empty({ icon: Icon, title, text }) {
  return (
    <Card className="rounded-3xl p-8 text-center border-dashed">
      <Icon className="h-10 w-10 mx-auto opacity-40" />
      <p className="mt-3 font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </Card>
  );
}

/* ============================== REORDER DIALOG ============================== */
function ReorderDialog({ state, onClose, onDone, uid }) {
  const [items, setItems] = useState(state.items);
  const [step, setStep] = useState("meds");
  const [locating, setLocating] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [loadedPharmacies, setLoadedPharmacies] = useState(false);
  const [pharmacy, setPharmacy] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState("pickup");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setItem = (i, key, val) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((arr) => [...arr, { name: "", dosage: "", quantity: "" }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const findPharmacies = useCallback(() => {
    setLocating(true);
    const fetchFor = async (lat, lng) => {
      try { const res = await pharmacyApi.nearby(lat, lng, 20); setPharmacies(res.data?.pharmacies || []); }
      catch { setPharmacies([]); } finally { setLoadedPharmacies(true); setLocating(false); }
    };
    if (!navigator.geolocation) { fetchFor(19.076, 72.8777); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchFor(pos.coords.latitude, pos.coords.longitude),
      () => { toast.error("Enable location to find pharmacies near you"); fetchFor(19.076, 72.8777); },
      { timeout: 8000 }
    );
  }, []);

  const goPharmacy = () => {
    if (items.filter((i) => i.name.trim()).length === 0) { toast.error("Add at least one medicine"); return; }
    setStep("pharmacy"); if (!loadedPharmacies) findPharmacies();
  };
  const submit = async () => {
    if (!pharmacy) { toast.error("Select a pharmacy"); return; }
    if (deliveryMode === "delivery" && !address.trim()) { toast.error("Enter a delivery address"); return; }
    setSubmitting(true);
    try {
      await pharmacyApi.createOrder(uid, {
        pharmacy_id: pharmacy.id, pharmacy_name: pharmacy.name, source_prescription_id: state.sourcePrescriptionId,
        items: items.filter((i) => i.name.trim()), delivery_mode: deliveryMode,
        delivery_address: deliveryMode === "delivery" ? address.trim() : null, notes: notes.trim(),
      });
      toast.success("Reorder request sent to the pharmacy"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reorder"); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-card p-5 space-y-4 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reorder-dialog">
        <div className="flex items-center justify-between"><h3 className="text-lg font-extrabold">Reorder medicines</h3><button onClick={onClose} data-testid="reorder-close"><X className="h-5 w-5 text-muted-foreground" /></button></div>

        {step === "meds" && (<>
          <p className="text-sm text-muted-foreground">Confirm the medicines you want to reorder.</p>
          <div className="space-y-2">{items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Medicine name *" className="rounded-xl flex-1" data-testid={`item-name-${i}`} />
              <Input value={it.dosage} onChange={(e) => setItem(i, "dosage", e.target.value)} placeholder="Dose" className="rounded-xl w-20" />
              <Input value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty" className="rounded-xl w-16" />
              {items.length > 1 && <button onClick={() => removeItem(i)} className="text-muted-foreground hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}</div>
          <Button variant="outline" onClick={addItem} className="rounded-xl gap-1.5 w-full" data-testid="add-item"><Plus className="h-4 w-4" /> Add another</Button>
          <Button onClick={goPharmacy} className="w-full rounded-2xl" style={{ backgroundColor: TEAL }} data-testid="to-pharmacy">Choose pharmacy</Button>
        </>)}

        {step === "pharmacy" && (<>
          <p className="text-sm text-muted-foreground">Select a partner pharmacy near you.</p>
          {locating ? (<div className="flex items-center justify-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Finding pharmacies…</div>
          ) : pharmacies.length === 0 ? (
            <Card className="rounded-2xl p-6 text-center border-dashed"><Store className="h-8 w-8 mx-auto opacity-40" /><p className="mt-2 text-sm text-muted-foreground">No partner pharmacies near you yet. Please check back soon.</p><Button variant="ghost" className="mt-2 text-sm" onClick={findPharmacies} style={{ color: TEAL }}>Retry</Button></Card>
          ) : (
            <div className="space-y-2">{pharmacies.map((p) => (
              <button key={p.id} onClick={() => setPharmacy(p)} data-testid={`pharmacy-${p.id}`}
                className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 transition-colors ${pharmacy?.id === p.id ? "border-2" : "border-border hover:bg-accent"}`}
                style={pharmacy?.id === p.id ? { borderColor: TEAL, backgroundColor: TEAL + "0D" } : {}}>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL + "1A" }}><Store className="h-5 w-5" style={{ color: TEAL }} /></div>
                <div className="min-w-0 flex-1"><p className="font-semibold truncate">{p.name}</p><p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{p.distanceKm != null ? `${p.distanceKm} km` : ""}{p.homeDelivery ? " • Home delivery" : ""}{p.is24x7 ? " • 24×7" : ""}</p></div>
                {pharmacy?.id === p.id && <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: TEAL }} />}
              </button>
            ))}</div>
          )}
          <Button onClick={() => setStep("confirm")} disabled={!pharmacy} className="w-full rounded-2xl" style={{ backgroundColor: TEAL }} data-testid="to-confirm">Continue</Button>
        </>)}

        {step === "confirm" && (<>
          <div className="rounded-2xl bg-accent p-3"><p className="text-sm font-semibold">{pharmacy?.name}</p><p className="text-xs text-muted-foreground">{itemsSummary(items.filter((i) => i.name.trim()))}</p></div>
          <div><p className="text-sm font-semibold mb-2">How would you like to get it?</p>
            <div className="flex gap-2">{[{ k: "pickup", label: "Pickup", icon: ShoppingBag }, { k: "delivery", label: "Home delivery", icon: Truck }].map((m) => { const MIcon = m.icon; const active = deliveryMode === m.k; return (
              <button key={m.k} onClick={() => setDeliveryMode(m.k)} data-testid={`mode-${m.k}`} className={`flex-1 rounded-2xl border p-3 flex flex-col items-center gap-1 transition-colors ${active ? "border-2" : "border-border"}`} style={active ? { borderColor: TEAL, backgroundColor: TEAL + "0D", color: TEAL } : {}}><MIcon className="h-5 w-5" /> <span className="text-sm font-semibold">{m.label}</span></button>
            ); })}</div>
          </div>
          {deliveryMode === "delivery" && <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address *" className="rounded-xl" data-testid="delivery-address" />}
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the pharmacy (optional)" className="rounded-xl" data-testid="reorder-notes" />
          <Button onClick={submit} disabled={submitting} className="w-full rounded-2xl" style={{ backgroundColor: TEAL }} data-testid="submit-reorder">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reorder request"}</Button>
        </>)}
      </div>
    </div>
  );
}
